import { useEffect } from 'react';
import { useDisplayStore } from '../store';
import { resolvePlayerLine, type PlayerLine } from './playerLine';
import type { GameSnapshot, ProjectedGameState } from '../socket';

export interface StatsRow { id: string; line: PlayerLine; }

// Resolves on-court rows for a side, registers them as flashable, and returns
// the current flash target + the side's team color. Shared by the stats widget
// and bespoke designs so the PLAYER_SCORED row-flash behaves identically.
export function usePlayerStatsRows(
  side: 'home' | 'away',
  snapshot: GameSnapshot | null,
  state: ProjectedGameState | null,
  isSample: boolean,
  sample: PlayerLine[],
): { rows: StatsRow[]; flashPlayerId: string | null; flashColor: string } {
  const setFlashableRows = useDisplayStore((s) => s.setFlashableRows);
  const flashPlayerId = useDisplayStore((s) => s.flashPlayerId);
  const roster = snapshot?.rosters[side];
  const onCourt = state?.onCourt?.[side] ?? [];

  const rows: StatsRow[] = (() => {
    if (isSample || !roster) return sample.map((p) => ({ id: '', line: p }));
    const inGame =
      onCourt.length > 0
        ? onCourt.map((id) => roster.players.find((p) => p.id === id)).filter(Boolean)
        : roster.players.slice(0, 5);
    return (inGame as NonNullable<typeof roster.players>)
      .map((p) => ({ id: p.id, line: resolvePlayerLine(snapshot, state, side, p.id) }))
      .filter((r): r is StatsRow => r.line != null);
  })();

  const rowIdsKey = rows.map((r) => r.id).filter(Boolean).join(',');
  useEffect(() => {
    setFlashableRows(side, rowIdsKey ? rowIdsKey.split(',') : []);
  }, [side, rowIdsKey, setFlashableRows]);

  const flashColor =
    (side === 'home' ? snapshot?.rosters.home.colorPrimary : snapshot?.rosters.away.colorPrimary)
    ?? (side === 'home' ? '#b3324a' : '#3a6df0');

  return { rows, flashPlayerId, flashColor };
}
